/**
 * Nordstua wishlist — localStorage based.
 * Key intentionally remains stable to preserve existing saved items.
 * Items: { id, handle, title, price, image, category, available, savedAt }
 */
(function () {
  if (window.NordstuaWishlist) {
    window.NordstuaWishlist.bind();
    return;
  }
  const KEY = 'nordstua-wishlist';
  const listeners = new Set();
  let sessionItems;

  function normalize(list) {
    if (!Array.isArray(list)) return [];
    const handles = new Set();
    return list.filter(function (item) {
      if (!item || typeof item !== 'object' || typeof item.handle !== 'string') return false;
      if (!/^[\p{L}\p{N}][\p{L}\p{N}-]*$/u.test(item.handle) || item.handle.length > 255) return false;
      if (handles.has(item.handle)) return false;
      handles.add(item.handle);
      return true;
    }).map(function (item) {
      return Object.assign({}, item, {
        title: typeof item.title === 'string' ? item.title : item.handle,
        image: typeof item.image === 'string' ? item.image : '',
        category: typeof item.category === 'string' ? item.category : '',
        savedAt: Number.isFinite(Number(item.savedAt)) ? Number(item.savedAt) : 0,
      });
    });
  }

  function read() {
    if (sessionItems) return normalize(sessionItems);
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return normalize(list);
    } catch (error) {
      return [];
    }
  }

  function write(list) {
    list = normalize(list);
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (error) {
      // Keep saving functional for this visit when browser storage is blocked.
      sessionItems = list;
      notify(list);
      return true;
    }

    sessionItems = undefined;
    notify(list);
    return true;
  }

  function notify(list) {
    updateControls(list);
    listeners.forEach(function (listener) {
      try {
        listener(list);
      } catch (error) {}
    });
  }

  function updateControls(list) {
    const items = list || read();
    const handles = new Set(items.map(function (item) {
      return item.handle;
    }));

    document.querySelectorAll('[data-wishlist-count]').forEach(function (element) {
      element.textContent = String(items.length);
      element.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      const active = handles.has(button.dataset.handle);
      const label = active
        ? button.dataset.removeLabel || 'Remove from wishlist'
        : button.dataset.addLabel || 'Add to wishlist';
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    });
  }

  function itemFromButton(button) {
    return {
      id: button.dataset.id || '',
      handle: button.dataset.handle || '',
      title: button.dataset.title || button.dataset.handle || '',
      price: button.dataset.price || '',
      priceCents: Number(button.dataset.priceCents),
      currency: button.dataset.currency || '',
      image: button.dataset.image || '',
      category: button.dataset.category || '',
      available: button.dataset.available !== 'false',
    };
  }

  function toggle(item) {
    if (!normalize([item]).length) return false;

    const list = read();
    const index = list.findIndex(function (savedItem) {
      return savedItem.handle === item.handle;
    });

    if (index >= 0) {
      list.splice(index, 1);
    } else {
      list.unshift(Object.assign({}, item, { savedAt: Date.now() }));
    }

    return write(list) && index < 0;
  }

  function has(handle) {
    return read().some(function (item) {
      return item.handle === handle;
    });
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    toggle(itemFromButton(button));
  });

  window.NordstuaWishlist = {
    read: read,
    write: write,
    toggle: toggle,
    has: has,
    bind: function () {
      updateControls();
    },
    subscribe: function (listener) {
      listeners.add(listener);
      return function () {
        listeners.delete(listener);
      };
    },
  };

  document.dispatchEvent(new CustomEvent('nordstua:wishlist:ready'));

  function initialize() {
    updateControls();

    const observer = new MutationObserver(function (mutations) {
      const hasNewWishlistControl = mutations.some(function (mutation) {
        return Array.from(mutation.addedNodes).some(function (node) {
          return (
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('[data-wishlist-toggle], [data-wishlist-count]') ||
              node.querySelector?.('[data-wishlist-toggle], [data-wishlist-count]'))
          );
        });
      });

      if (hasNewWishlistControl) updateControls();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', function () {
    updateControls();
  });

  window.addEventListener('storage', function (event) {
    if (event.key === KEY || event.key === null) notify(read());
  });
})();
